import React, { useState } from 'react';

import { useTranslation } from 'react-i18next';

import { InfoCard } from '../../info-card';
import useGetRelativityFieldMapping from '@/api/queries/useGetRelativityFieldMapping';
import { RelativityWorkspaceType } from '@/api/types/backend-dto-types';
import { Icon } from '@/new/components/icon';
import { Button } from '@/new/components/shadcn/button';
import { DialogHeader, DialogTitle, DialogDescription } from '@/new/components/shadcn/dialog';
import { Input } from '@/new/components/shadcn/input';
import { Skeleton } from '@/new/components/shadcn/skeleton';
import { useRelativityWorkspaceSearch } from '@/new/hooks/useRelativityWorkspaceSearch';

interface RelativityWorkspacesModalProps {
  onSelectWorkspace: (workspace: RelativityWorkspaceType) => void;
  onClose: () => void;
  linkedWorkspaceId?: number | null;
}

interface WorkspaceItemProps {
  workspace: RelativityWorkspaceType;
  isSelected: boolean;
  isDisabled: boolean;
  onSelect: (workspace: RelativityWorkspaceType) => void;
}

const WorkspaceItem: React.FC<WorkspaceItemProps> = ({ workspace, isSelected, isDisabled, onSelect }) => {
  return (
    <div
      className={`group rounded-md border p-2.5 transition-all duration-200 ${
        isDisabled
          ? 'cursor-not-allowed border-gray-200 opacity-50'
          : isSelected
            ? 'cursor-pointer border-teal-500 bg-teal-50/50 ring-teal-500'
            : 'cursor-pointer border-gray-200 hover:border-teal-500/30 hover:bg-teal-500/5'
      }`}
      onClick={() => !isDisabled && onSelect(workspace)}
    >
      <div className="flex min-w-0 flex-1 items-center space-x-2.5">
        <div
          className={`flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-md ${
            isSelected ? 'bg-teal-500/20' : 'bg-gray-100 group-hover:bg-teal-500/15'
          }`}
        >
          <Icon
            className={`lucide--building-2 ${isSelected ? 'text-teal-500' : 'text-gray-600 group-hover:text-teal-500'}`}
          />
        </div>
        <div className="min-w-0 flex-1">
          <h3
            className={`truncate text-sm font-medium ${
              isSelected ? 'text-teal-900' : 'text-gray-900 group-hover:text-teal-900'
            }`}
            title={workspace.name}
          >
            {workspace.name}
          </h3>
          <p className="truncate text-xs text-gray-500" title={`ID: ${workspace.artifactId}`}>
            ID: {workspace.artifactId}
          </p>
        </div>
      </div>
    </div>
  );
};

export const RelativityWorkspacesModal: React.FC<RelativityWorkspacesModalProps> = ({
  onSelectWorkspace,
  linkedWorkspaceId,
}) => {
  const { t } = useTranslation();
  const [selectedWorkspace, setSelectedWorkspace] = useState<RelativityWorkspaceType | null>(null);

  const {
    workspacesData,
    searchQuery,
    setSearchQuery,
    currentPage,
    canPrevious,
    canNext,
    goToPreviousPage,
    goToNextPage,
    isLoading,
    error,
  } = useRelativityWorkspaceSearch({ pageSize: 5 });

  // Check if selected workspace has field mappings configured
  const { data: fieldMapping } = useGetRelativityFieldMapping({
    workspaceId: selectedWorkspace?.artifactId ?? null,
    enabled: !!selectedWorkspace,
  });

  const handleSelectWorkspace = (workspace: RelativityWorkspaceType) => {
    setSelectedWorkspace(workspace);
  };

  const handleConfirmSelection = () => {
    if (selectedWorkspace) {
      onSelectWorkspace(selectedWorkspace);
    }
  };

  const handleSearchChange = (value: string) => {
    setSearchQuery(value);
    setSelectedWorkspace(null); // Clear selection when searching
  };

  const totalPages = workspacesData?.totalPages || 0;

  return (
    <div>
      <DialogHeader>
        <DialogTitle>
          <Icon className="lucide--workflow" />
          Select Relativity Workspace
        </DialogTitle>
        <DialogDescription>Choose a workspace to import documents from Relativity.</DialogDescription>
      </DialogHeader>

      {/* Workspace Restriction Banner */}
      {linkedWorkspaceId && (
        <div className="mb-4 rounded-md border border-amber-300 bg-amber-50 p-3">
          <div className="flex items-start gap-2">
            <div className="flex-1">
              <div className="flex items-center gap-2">
                <Icon className="lucide--info size-3 flex-shrink-0 text-amber-700" />

                <p className="text-xs text-amber-700">
                  This matter is already linked to a Relativity workspace. Documents can only be imported from that
                  workspace.
                </p>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Search and Pagination */}
      <div className="mb-4 flex items-center gap-3">
        <div className="relative flex-1">
          <Icon className="lucide--search absolute top-1/2 left-3 h-4 w-4 -translate-y-1/2 text-gray-400" />
          <Input
            type="text"
            placeholder="Search workspaces..."
            value={searchQuery}
            onChange={(e) => handleSearchChange(e.target.value)}
            className="pl-10"
          />
        </div>

        {/* Inline Pagination */}
        {workspacesData && totalPages > 1 && (
          <div className="flex items-center gap-1">
            <Button
              onClick={goToPreviousPage}
              disabled={!canPrevious}
              variant="default"
              size="iconSm"
              aria-label="Previous page"
            >
              <Icon className="lucide--chevron-left" />
            </Button>
            <span className="px-2 text-sm text-gray-600 tabular-nums">
              {currentPage} / {totalPages}
            </span>
            <Button onClick={goToNextPage} disabled={!canNext} variant="default" size="iconSm" aria-label="Next page">
              <Icon className="lucide--chevron-right" />
            </Button>
          </div>
        )}
      </div>

      <div className="min-h-[280px]">
        {isLoading && (
          <div className="space-y-2" aria-busy="true" aria-label="Loading workspaces">
            {Array.from({ length: 5 }).map((_, i) => (
              <div key={i} className="flex items-center rounded-md border border-gray-200 p-2.5">
                <div className="flex min-w-0 flex-1 items-center space-x-2.5">
                  <Skeleton className="h-8 w-8 flex-shrink-0 rounded-md" />
                  <div className="min-w-0 flex-1 space-y-1.5">
                    <Skeleton className="h-4 w-3/4 max-w-[180px] rounded" />
                    <Skeleton className="h-3 w-1/2 max-w-[100px] rounded" />
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}

        {!!error && (
          <div className="my-2 flex gap-2 rounded-md border border-red-200 bg-red-50 px-3.5 py-2.5 text-[13px] text-red-600">
            <div>
              <Icon className="lucide--alert-circle mt-0.75 text-red-500" />
            </div>
            <div>Failed to load workspaces. {error instanceof Error ? error.message : 'An unknown error occurred'}</div>
          </div>
        )}

        {workspacesData && workspacesData.workspaces.length === 0 && !isLoading && (
          <div className="py-12 text-center">
            <Icon className="lucide--building-2 mx-auto h-10 w-10 text-gray-400" />
            <h3 className="mt-2 text-sm font-medium text-gray-900">
              {searchQuery ? 'No matching workspaces' : 'No workspaces found'}
            </h3>
            <p className="mt-1 text-sm text-gray-500">
              {searchQuery ? 'Try adjusting your search terms.' : "You don't have access to any Relativity workspaces."}
            </p>
          </div>
        )}

        {workspacesData && workspacesData.workspaces.length > 0 && (
          <div className="space-y-2">
            {workspacesData.workspaces.map((workspace) => (
              <WorkspaceItem
                key={workspace.artifactId}
                workspace={workspace}
                isSelected={selectedWorkspace?.artifactId === workspace.artifactId}
                isDisabled={
                  linkedWorkspaceId !== null &&
                  linkedWorkspaceId !== undefined &&
                  workspace.artifactId !== linkedWorkspaceId
                }
                onSelect={handleSelectWorkspace}
              />
            ))}
          </div>
        )}
      </div>

      {/* Metadata Extraction Status Banner */}
      {selectedWorkspace && fieldMapping && (
        <div className="my-4 space-y-2">
          <div className="flex gap-2 rounded-md border border-purple-200 bg-purple-50 px-3.5 py-2.5 text-[13px] text-purple-600">
            <div>
              <Icon className="lucide--check mt-0.75 text-purple-500" />
            </div>
            <div>Metadata extraction enabled. Document fields will be automatically mapped during import.</div>
          </div>

          {fieldMapping.parentIdentifierFieldKey && (
            <div className="flex gap-2 rounded-md border border-purple-200 bg-purple-50 px-3.5 py-2.5 text-[13px] text-purple-600">
              <div>
                <Icon className="lucide--git-branch mt-0.75 text-purple-500" />
              </div>
              <div>
                Parent identification enabled. Wexler will use the configured Parent Control Number field to link
                imported documents to their parent files after import.
              </div>
            </div>
          )}
        </div>
      )}

      {selectedWorkspace && !fieldMapping && (
        <div className="my-4 flex gap-2 rounded-md border border-amber-200 bg-amber-50 px-3.5 py-2.5 text-[13px] text-amber-600">
          <div>
            <Icon className="lucide--info mt-0.75 text-amber-500" />
          </div>
          <div>
            No metadata mapping configured for this workspace. Documents will be imported without metadata extraction.
            Configure field mappings in your {t('organisation')} settings to enable this feature.
          </div>
        </div>
      )}

      {/* Action Buttons */}
      <div className="mt-4 flex items-center justify-between pl-1">
        {selectedWorkspace ? (
          <div className="animate-in fade-in flex w-full items-center justify-between duration-200">
            <div className="text-sm text-gray-500">
              <span className="font-medium text-gray-700">
                Selected Workspace: <span className="font-semibold text-gray-700">{selectedWorkspace.name}</span>
              </span>
            </div>
            <div className="flex justify-end">
              <Button onClick={handleConfirmSelection} className="gap-1.5">
                Next
                <Icon className="lucide--chevron-right" />
              </Button>
            </div>
          </div>
        ) : (
          <>
            <div className="text-sm text-gray-500" />
            <div className="flex justify-end" />
          </>
        )}
      </div>
    </div>
  );
};
